import { ref } from 'vue';

export const useLoadWithRetry = (config = {}) => {
  const maxRetry = config.max_retry || 3;
  const backoff = config.backoff || 1000;

  const isLoaded = ref(false);
  const hasError = ref(false);
  const isRetrying = ref(false);
  let lastUrl = null;

  const loadWithRetry = async url => {
    lastUrl = url;
    // Reset para permitir re-execução (reload) a partir do estado de erro.
    hasError.value = false;
    isRetrying.value = true;

    const attemptLoad = () => {
      return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          isLoaded.value = true;
          hasError.value = false;
          resolve();
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        img.src = url;
      });
    };

    const sleep = ms => {
      return new Promise(resolve => {
        setTimeout(resolve, ms);
      });
    };

    const retry = async (attempt = 0) => {
      try {
        await attemptLoad();
      } catch (error) {
        if (attempt + 1 >= maxRetry) {
          hasError.value = true;
          isLoaded.value = false;
          return;
        }
        await sleep(backoff * (attempt + 1));
        await retry(attempt + 1);
      }
    };

    await retry();
    isRetrying.value = false;
  };

  // Re-executa o ciclo de tentativas (limitado a maxRetry) para a última URL. Usado
  // pelo botão "tentar novamente" quando o auto-retry esgotou — ex.: corrida de escrita
  // do blob mais longa que a janela. NÃO amplia o limite: cada disparo é um novo ciclo
  // curto, iniciado pelo usuário (sem loop automático).
  const reload = () => (lastUrl ? loadWithRetry(lastUrl) : Promise.resolve());

  return {
    isLoaded,
    hasError,
    isRetrying,
    loadWithRetry,
    reload,
  };
};
